import React, { useState } from 'react'
import styles from './AppointmentsDisabilitiesController.module.sass'

const rowHeight = 52
const headerHeight = 32
const circleRadius = 10
const arrowLength = 7
const arrowWidth = 5

// Chevron pointing along +x, tip at the origin — rotated/translated per
// segment so it always points along the curve's own direction at that point.
const arrowPath = `M${-arrowLength},${-arrowWidth} L0,0 L${-arrowLength},${arrowWidth}`

// Point and tangent of a cubic Bezier at parameter t (De Casteljau-style).
const cubicBezierPoint = (p0, p1, p2, p3, t) => {
	const mt = 1 - t
	return {
		x:
			mt ** 3 * p0.x +
			3 * mt ** 2 * t * p1.x +
			3 * mt * t ** 2 * p2.x +
			t ** 3 * p3.x,
		y:
			mt ** 3 * p0.y +
			3 * mt ** 2 * t * p1.y +
			3 * mt * t ** 2 * p2.y +
			t ** 3 * p3.y,
	}
}

const cubicBezierTangent = (p0, p1, p2, p3, t) => {
	const mt = 1 - t
	return {
		x:
			3 * mt ** 2 * (p1.x - p0.x) +
			6 * mt * t * (p2.x - p1.x) +
			3 * t ** 2 * (p3.x - p2.x),
		y:
			3 * mt ** 2 * (p1.y - p0.y) +
			6 * mt * t * (p2.y - p1.y) +
			3 * t ** 2 * (p3.y - p2.y),
	}
}

const getLineElements = (points) => {
	if (points?.length) {
		const circles = []
		circles.push(
			<circle
				key={points[0].id}
				data-id={`${points[0].id}`}
				cx={`${points[0].x}`}
				cy={`${points[0].y}`}
				r={circleRadius}
				fill='blue'
				style={{ cursor: 'pointer' }}
			/>
		)

		const segments = []
		const arrows = []

		for (let i = 1; i < points.length; i++) {
			const prevPoint = points[i - 1]
			const currPoint = points[i]

			// Smooth corner control points
			const controlX1 = prevPoint.x
			const controlY1 =
				prevPoint.y + (currPoint.y - prevPoint.y) * (1 / 2)
			const controlX2 = currPoint.x
			const controlY2 =
				currPoint.y - (currPoint.y - prevPoint.y) * (1 / 2)

			segments.push(
				`M${prevPoint.x},${prevPoint.y} C${controlX1},${controlY1} ${controlX2},${controlY2} ${currPoint.x},${currPoint.y}`
			)

			// Place the arrow tip on the curve itself, a bit before the
			// destination node, and orient it using the curve's own tangent
			// there — so it tilts along with however the line is bending.
			const p0 = prevPoint
			const p1 = { x: controlX1, y: controlY1 }
			const p2 = { x: controlX2, y: controlY2 }
			const p3 = currPoint
			const chordLength =
				Math.sqrt((p3.x - p0.x) ** 2 + (p3.y - p0.y) ** 2) || 1
			const t = Math.max(0, 1 - circleRadius / chordLength)
			const { x: tipX, y: tipY } = cubicBezierPoint(p0, p1, p2, p3, t)
			const tangent = cubicBezierTangent(p0, p1, p2, p3, t)
			const angle = (Math.atan2(tangent.y, tangent.x) * 180) / Math.PI

			arrows.push(
				<path
					key={`arrow-${currPoint.id}`}
					className={styles['arrowhead']}
					d={arrowPath}
					fill='none'
					stroke='black'
					strokeWidth='2'
					strokeLinecap='round'
					strokeLinejoin='round'
					transform={`translate(${tipX},${tipY}) rotate(${angle})`}
				/>
			)

			const circleColor = i === points.length - 1 ? 'red' : 'orange'

			circles.push(
				<circle
					key={currPoint.id}
					data-id={`${currPoint.id}`}
					cx={`${currPoint.x}`}
					cy={`${currPoint.y}`}
					r={circleRadius}
					fill={circleColor}
					style={{ cursor: 'pointer' }}
				/>
			)
		}

		return [segments, arrows, circles]
	}

	return [[], [], []]
}

const PathsDiagram = ({
	paths,
	pointsMapper,
	pathColumns,
	selectedId,
	onRowClick,
}) => {
	const [hoveredSegment, setHoveredSegment] = useState(null)
	const [hoveredNode, setHoveredNode] = useState(null)

	const columnWidth = 120 / pathColumns.length
	const diagramHeight =
		Object.keys(pointsMapper).length * rowHeight + headerHeight
	const groups = paths.map((path, pathIndex) => {
		const pathPoints = path.map((appointment) => {
			const point = pointsMapper[appointment.id] ?? { x: 0, y: 0 }

			return {
				x: point.x * columnWidth + columnWidth / 2,
				y: point.y * rowHeight + headerHeight + rowHeight / 2,
				id: appointment.id,
			}
		})

		const [segments, arrows, circles] = getLineElements(pathPoints)

		const isPathSelected = path.some(
			(item) => String(item.id) === String(selectedId)
		)

		const isPathHoveringOwnSegment =
			hoveredSegment?.pathIndex === pathIndex ||
			hoveredNode?.pathIndex === pathIndex
		const isGroupVisible = isPathSelected || isPathHoveringOwnSegment
		const isSomethingActive =
			!!selectedId || hoveredSegment != null || hoveredNode != null

		const handleGroupClick = (e) => {
			const clickedId = e.target.dataset.id
			if (clickedId) {
				onRowClick?.(clickedId)
			} else if (isPathSelected) {
				onRowClick?.(selectedId)
			} else {
				onRowClick?.(String(path[0].id))
			}
		}

		const lines = segments.map((segment, index) => {
			const isSegmentHovered =
				(hoveredSegment?.pathIndex === pathIndex &&
					hoveredSegment?.segIndex === index) ||
				(hoveredNode?.pathIndex === pathIndex &&
					hoveredNode?.nodeIndex === index)

			return (
				<path
					key={`${path[0]?.id}-${index}`}
					className={isSegmentHovered ? styles['segment-active'] : ''}
					fill='none'
					stroke='black'
					strokeWidth='2'
					d={segment}
					style={{ cursor: 'pointer' }}
					onMouseEnter={() =>
						setHoveredSegment({ pathIndex, segIndex: index })
					}
					onMouseLeave={() => setHoveredSegment(null)}
				></path>
			)
		})

		const styledArrows = arrows.map((arrow, index) => {
			const isSegmentHovered =
				(hoveredSegment?.pathIndex === pathIndex &&
					hoveredSegment?.segIndex === index) ||
				(hoveredNode?.pathIndex === pathIndex &&
					hoveredNode?.nodeIndex === index)

			return React.cloneElement(arrow, {
				className: `${styles['arrowhead']} ${
					isSegmentHovered ? styles['segment-active'] : ''
				}`,
			})
		})

		const styledCircles = circles.map((circle, index) => {
			const isNodeHovered =
				hoveredNode?.pathIndex === pathIndex &&
				hoveredNode?.nodeIndex === index

			return React.cloneElement(circle, {
				className: isNodeHovered ? styles['node-active'] : '',
				onMouseEnter: () =>
					setHoveredNode({ pathIndex, nodeIndex: index }),
				onMouseLeave: () => setHoveredNode(null),
			})
		})

		return (
			<g
				key={`${path[0]?.id}-${pathIndex}`}
				className={`${styles['line']} ${
					isPathSelected ? styles['--selected'] : ''
				} ${isGroupVisible ? styles['--visible'] : ''} ${
					isSomethingActive && !isGroupVisible
						? styles['--dimmed']
						: ''
				}`}
				onClick={handleGroupClick}
			>
				{lines}
				{styledArrows}
				{styledCircles}
			</g>
		)
	})

	return (
		<svg
			width='120'
			height={`${diagramHeight}`}
			viewBox={`0 0 120 ${diagramHeight}`}
		>
			{groups}
		</svg>
	)
}

export default PathsDiagram
